import { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faFileCsv, faSpinner, faUpload, faCircleCheck, faTriangleExclamation, faCircleInfo } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { getUserFacingError } from '@/utils/errorMessages';
import { showSuccessToast } from '@/utils/toasts.ts';
import { FILE_SIZE_LIMITS, formatFileSize } from '@/config/limits';
import {
  EVENT_IMPORT_CSV_HEADER,
  EVENT_IMPORT_SPECIAL_AUDIENCES,
  EVENT_IMPORT_AUDIENCE_ALIASES,
  EVENT_IMPORT_AUDIENCE_PREFIXES,
  EVENT_IMPORT_AUDIENCE_SUFFIXES,
  EVENT_IMPORT_AUDIENCE_SEPARATORS,
  EVENT_IMPORT_REMINDER_UNIT_ALIASES,
  ALL_REMINDER_UNITS,
  REMINDER_UNIT_LABELS,
  MAX_EVENT_REMINDER_COUNT,
  formatEventDateTime,
  formatReminderOffsetList
} from '@/utils/event-helpers';
import type { EventImportResult, EventImportRowStatus } from '@/models';
import { EVENT_IMPORT_ROW_STATUS_LABELS, ALL_FEATURE_TYPES, FEATURE_TYPE_LABELS } from '@/models';

interface ImportEventsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (result: EventImportResult) => void;
}

// SKIPPED_EXISTING is a normal outcome, not a problem: the import is idempotent over
// subject+start so an extended file can simply be uploaded again
const STATUS_BADGE_COLORS: Record<EventImportRowStatus, string> = {
  'VALID':            'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  'CREATED':          'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  'SKIPPED_EXISTING': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  'INVALID':          'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
};

export default function ImportEventsModal({ isOpen, onClose, onImported }: ImportEventsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<EventImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setDragOver(false);
  };

  const handleFileSelect = async (selectedFile: File) => {
    if (selectedFile.size > FILE_SIZE_LIMITS.EVENT_IMPORT_CSV_MAX) {
      setError(`Die Datei ist zu groß (max. ${formatFileSize(FILE_SIZE_LIMITS.EVENT_IMPORT_CSV_MAX)})`);
      return;
    }

    setError(null);
    setResult(null);
    setFile(selectedFile);

    await runImport(selectedFile, true);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      void handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      void handleFileSelect(droppedFile);
    }
  };

  // The file is handed over as a Blob so its bytes reach the backend untouched - decoding
  // it to a string here would re-encode it as UTF-8 and corrupt Windows-1252 umlauts.
  // The backend detects UTF-8 vs Windows-1252 itself.
  const runImport = async (content: File, dryRun: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const response = await APIRoutes.importEvents(content, dryRun);
      setResult(response);

      if (!dryRun && response.ok) {
        showSuccessToast(
          response.createdCount > 0
            ? `${response.createdCount} Event(s) importiert`
            : 'Keine neuen Events - alle Zeilen existierten bereits'
        );
        onImported(response);
      }
    } catch (err) {
      console.error('Error importing events:', err);
      // Whole-file failures (empty, header-only, wrong column count, undecodable) come
      // back as an APIError instead of an import result, all under INVALID_REQUEST
      setError(getUserFacingError(err, 'Der Import ist fehlgeschlagen', {
        INVALID_REQUEST: 'Die CSV-Datei konnte nicht gelesen werden. Bitte prüfen Sie, dass jede Zeile 7 oder 8 mit Semikolon getrennte Spalten hat, die Datei Datenzeilen enthält und als UTF-8 oder Windows-1252 gespeichert ist.',
        FORBIDDEN: 'Nur Administratoren dürfen Events importieren.'
      }));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const canCommit = result !== null && result.ok && result.dryRun && file !== null && result.createdCount > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Events aus CSV importieren"
      icon={<FontAwesomeIcon icon={faFileCsv} className="w-5 h-5 text-purple-600" />}
      maxWidth="4xl"
      closeDisabled={loading}
    >
      <div className="p-6">
        {error !== null && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Format help */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="text-sm text-purple-600 dark:text-purple-400 hover:underline cursor-pointer flex items-center"
          >
            <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4 mr-2" />
            {showHelp ? 'Format-Hinweise ausblenden' : 'Welches Format braucht die CSV?'}
          </button>

          {showHelp && (
            <div className="mt-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <p>Semikolon-getrennt, 7 oder 8 Spalten — die 8. Spalte „Erinnerungen" ist optional. Die Kopfzeile ist optional:</p>
              <pre className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 text-xs">
                {EVENT_IMPORT_CSV_HEADER}
              </pre>
              <ul className="list-disc list-inside space-y-1 text-xs text-gray-600 dark:text-gray-400">
                <li><strong>Eventtyp</strong> — Betreff, Pflichtfeld</li>
                <li><strong>Wochentag</strong> — optional, wird gegen das Datum geprüft (z.B. „Mittwoch")</li>
                <li><strong>Datum</strong> — <code>TT.MM.JJJJ</code>, wird in Europe/Berlin ausgewertet</li>
                <li><strong>Beginn / Ende</strong> — <code>HH:MM</code> Ortszeit, Sommer-/Winterzeit wird automatisch berücksichtigt</li>
                <li><strong>Google Meet Link</strong> — optional, muss mit <code>http://</code> oder <code>https://</code> beginnen</li>
                <li><strong>Zielgruppe</strong> — siehe die vollständige Liste unten</li>
                <li><strong>Erinnerungen</strong> — optional, Erinnerungsmails vor Beginn (siehe unten). Leer oder Spalte weglassen = keine Erinnerungen</li>
              </ul>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Der Import ist ganz oder gar nicht: ist eine Zeile fehlerhaft, wird nichts geschrieben. Zeilen, deren Betreff und Startzeit bereits existieren, werden übersprungen — eine erweiterte Datei kann also einfach erneut hochgeladen werden. UTF-8 und Windows-1252 (Excel-Standard) werden beide akzeptiert.
              </p>

              {/* Zielgruppe - every accepted value */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Spalte „Zielgruppe" — alle erlaubten Werte
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Groß-/Kleinschreibung und zusätzliche Leerzeichen sind egal.
                </p>

                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Feste Werte</p>
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full text-xs">
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {EVENT_IMPORT_SPECIAL_AUDIENCES.map((entry) => (
                        <tr key={entry.meaning}>
                          <td className="py-1.5 pr-4 align-top">
                            <div className="flex flex-wrap gap-1">
                              {entry.values.map((v) => (
                                <code key={v} className="px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 whitespace-nowrap">
                                  {v}
                                </code>
                              ))}
                            </div>
                          </td>
                          <td className="py-1.5 align-top text-gray-600 dark:text-gray-400">{entry.meaning}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Features</p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400">
                        <th className="py-1 pr-4 text-left font-medium">Feature</th>
                        <th className="py-1 text-left font-medium">Erlaubte Schreibweisen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {ALL_FEATURE_TYPES.map((type) => (
                        <tr key={type}>
                          <td className="py-1.5 pr-4 align-top whitespace-nowrap text-gray-700 dark:text-gray-300">
                            {FEATURE_TYPE_LABELS[type]}
                          </td>
                          <td className="py-1.5 align-top">
                            <div className="flex flex-wrap gap-1">
                              {EVENT_IMPORT_AUDIENCE_ALIASES[type].map((alias) => (
                                <code key={alias} className="px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 whitespace-nowrap">
                                  {alias}
                                </code>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="list-disc list-inside space-y-1 text-xs text-gray-600 dark:text-gray-400 mt-3">
                  <li>
                    Diese Vorsätze werden ignoriert:{' '}
                    {EVENT_IMPORT_AUDIENCE_PREFIXES.map((p) => <code key={p} className="mr-1">{p}</code>)}
                  </li>
                  <li>
                    Diese Endungen werden ignoriert:{' '}
                    {EVENT_IMPORT_AUDIENCE_SUFFIXES.map((s) => <code key={s} className="mr-1">{s}</code>)}
                    {' '}— <code>Boost-Mitglieder</code> ist also dasselbe wie <code>boost</code>
                  </li>
                  <li>
                    Mehrere Features lassen sich mit{' '}
                    {EVENT_IMPORT_AUDIENCE_SEPARATORS.map((s) => <code key={s} className="mr-1">{s}</code>)}
                    {' '}verbinden. Sie werden ODER-verknüpft: ein Nutzer braucht mindestens eines davon.
                  </li>
                  <li>
                    Beispiel: <code>Boost- und Community-Mitglieder</code> → sichtbar für Nutzer mit Boost <em>oder</em> Community-Mitgliedschaft.
                  </li>
                </ul>
              </div>

              {/* Erinnerungen - accepted formats */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Spalte „Erinnerungen" — automatische Erinnerungsmails
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Mehrere Zeitpunkte mit <strong>Komma</strong> trennen (das Semikolon trennt die Spalten).
                  Jeder Eintrag ist eine ganze Zahl mit optionaler Einheit — ohne Einheit sind es Minuten.
                  Beispiel: <code>1 Tag, 2 Std, 30 Min</code> oder <code>1440, 120, 30</code>.
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400">
                        <th className="py-1 pr-4 text-left font-medium">Einheit</th>
                        <th className="py-1 text-left font-medium">Erlaubte Schreibweisen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {ALL_REMINDER_UNITS.map((unit) => (
                        <tr key={unit}>
                          <td className="py-1.5 pr-4 align-top whitespace-nowrap text-gray-700 dark:text-gray-300">
                            {REMINDER_UNIT_LABELS[unit]}
                          </td>
                          <td className="py-1.5 align-top">
                            <div className="flex flex-wrap gap-1">
                              {EVENT_IMPORT_REMINDER_UNIT_ALIASES[unit].map((alias) => (
                                <code key={alias} className="px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 whitespace-nowrap">
                                  {alias}
                                </code>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="list-disc list-inside space-y-1 text-xs text-gray-600 dark:text-gray-400 mt-3">
                  <li>Groß-/Kleinschreibung, Leerzeichen und ein Punkt hinter der Einheit sind egal (<code>2 Std.</code> = <code>2std</code>).</li>
                  <li>Maximal {MAX_EVENT_REMINDER_COUNT} Erinnerungen pro Event, jeweils zwischen 1 Minute und 30 Tagen vor Beginn. Keine doppelten Zeitpunkte (<code>60</code> und <code>1 Std</code> sind derselbe Zeitpunkt).</li>
                  <li>Nur ganze Zahlen — <code>1,5 Std</code> wird als zwei Erinnerungen gelesen (1 Minute und 5 Stunden).</li>
                  <li>Leere Zelle = keine Erinnerungen. Bereits existierende (übersprungene) Events werden <strong>nicht</strong> nachträglich geändert.</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* File picker */}
        {file === null ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faUpload} className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              CSV hierher ziehen oder klicken zum Auswählen
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              max. {formatFileSize(FILE_SIZE_LIMITS.EVENT_IMPORT_CSV_MAX)}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <FontAwesomeIcon icon={faFileCsv} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
            </div>
            <button
              type="button"
              onClick={reset}
              disabled={loading}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 cursor-pointer"
            >
              Entfernen
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          className="hidden"
          accept=".csv,text/csv"
        />

        {loading && (
          <div className="flex justify-center items-center py-8">
            <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-purple-600 dark:text-purple-400 animate-spin mr-3" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Datei wird geprüft...</span>
          </div>
        )}

        {/* Result */}
        {result !== null && !loading && (
          <div className="mt-6">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Zeilen</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{result.totalRows}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">{result.dryRun ? 'Werden angelegt' : 'Angelegt'}</p>
                <p className="text-lg font-semibold text-green-700 dark:text-green-300">{result.createdCount}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Übersprungen</p>
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">{result.skippedCount}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">Fehlerhaft</p>
                <p className="text-lg font-semibold text-red-700 dark:text-red-300">{result.invalidCount}</p>
              </div>
            </div>

            {!result.ok && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-start">
                <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
                <span>
                  Es wurde nichts geschrieben. Bitte korrigieren Sie die markierten Zeilen in der Datei und laden Sie sie erneut hoch.
                </span>
              </div>
            )}

            {result.ok && !result.dryRun && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300 flex items-start">
                <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
                <span>Import abgeschlossen.</span>
              </div>
            )}

            {/* Rows */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Zeile</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Betreff</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Start</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Zielgruppe</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Erinnerungen</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {result.rows.map((row) => (
                      <tr key={row.line} className={row.status === 'INVALID' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{row.line}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                          {row.subject.length > 0 ? row.subject : <span className="text-gray-400 italic">leer</span>}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {row.start !== null ? formatEventDateTime(row.start) : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                          {row.featureFilter === null && row.status !== 'INVALID' ? 'Öffentlich' : (row.audience.length > 0 ? row.audience : '-')}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {(row.reminderMinutes ?? []).length > 0 ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
                              title={`Erinnerungsmails ${formatReminderOffsetList(row.reminderMinutes ?? [])} vor Beginn`}
                            >
                              <FontAwesomeIcon icon={faBell} className="w-3 h-3 mr-1" />
                              {formatReminderOffsetList(row.reminderMinutes ?? [])}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-600">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_COLORS[row.status]}`}>
                            {EVENT_IMPORT_ROW_STATUS_LABELS[row.status]}
                          </span>
                          {row.error !== null && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{row.error}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            {result !== null && !result.dryRun ? 'Schließen' : 'Abbrechen'}
          </button>
          {canCommit && (
            <button
              type="button"
              onClick={() => file !== null && void runImport(file, false)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 cursor-pointer flex items-center"
            >
              {result.createdCount} Event(s) importieren
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
